// Copyright 2025, University of Maryland, All Rights Reserved
import { Slider } from "@/components/ui/slider"
import { Label } from "@/components/ui/label"
import type { WorkspaceBillImpactScore } from "@/types/workspace-bill"
import { useConfig } from "@/hooks/config/useConfig"
import { getSchemaForType } from "@/utils/schema/schema-type-utils"
import { getFilterScoreRange, formatScoreValue, generateStepLabels } from "@/utils/schema/schema-range-utils"
import { useMemo } from "react"

interface WorkspaceBillsPageFiltersProps {
  slider_value: WorkspaceBillImpactScore
  debounced_min_impact_score: WorkspaceBillImpactScore
  handle_slider_change: (value: number[]) => void
}

export function WorkspaceBillsPageFilters({
  slider_value,
  debounced_min_impact_score,
  handle_slider_change,
}: WorkspaceBillsPageFiltersProps) {
  const { config } = useConfig()
  
  // Get the monitor schema and extract filter score range
  const { min, max, step } = useMemo(() => {
    const schema = getSchemaForType(config || undefined, 'monitor')
    return getFilterScoreRange(schema)
  }, [config])
  
  // Generate step labels for the slider
  const stepLabels = useMemo(() => {
    return generateStepLabels(min, max)
  }, [min, max])
  
  return (
    <div className="space-y-2">
      <Label htmlFor="impact-slider">
        Minimum Impact Score: {formatScoreValue(slider_value)}
        {slider_value !== debounced_min_impact_score && (
          <span className="text-xs text-muted-foreground ml-1">(updating...)</span>
        )}
      </Label>
      <Slider
        id="impact-slider"
        min={min}
        max={max}
        step={step}
        value={[slider_value]}
        onValueChange={handle_slider_change}
        className="w-full max-w-md"
      />
      <div className="flex justify-between text-xs text-muted-foreground max-w-md">
        {stepLabels.map(({ value, label }) => (
          <span key={value}>{label}</span>
        ))}
      </div>
    </div>
  )
}
